import type { GrantLevel } from '@discovr/api';
import { isBrandOwnerTier } from '@discovr/common';
// Single source of truth for CampaignPermissions is the capabilities store in
// @discovr/features (which holds the built object). Import it for the builder
// return types below and re-export so existing consumers keep importing it from
// this module.
import type { CampaignPermissions } from '@discovr/features';

export type { CampaignPermissions };

/**
 * Per-campaign grant LEVEL gates ACTIONS (mirrors backend grant_levels.go):
 *   read  → view only (every mutating flag false)
 *   write → full campaign lifecycle
 *   admin → write + manage access (Access tab)
 *
 * The backend sends `my_access_level` on the campaign-detail response and maps
 * super-admin / brand_owner / creator to "admin", so level alone drives gating.
 * NOTE: this is UX gating only — the backend authorizer is the security boundary.
 */
export const canWriteAtLevel = (level: GrantLevel | null | undefined): boolean =>
  level === 'write' || level === 'admin';

/**
 * Brand campaign permissions from the caller's role + effective per-campaign
 * level. Brand never holds admin-app-only capabilities (sheet upload,
 * approve-on-behalf, remove, submit-on-behalf, send-brief). `level` falls back
 * to admin for brand_owner / read otherwise when absent (defensive — the backend
 * always sends my_access_level and maps owners to admin).
 */
export function buildBrandPermissions(
  role: 'brand_owner' | 'brand_admin' | 'brand_member' | string,
  level?: GrantLevel,
): CampaignPermissions {
  const eff: GrantLevel = level ?? (isBrandOwnerTier(role) ? 'admin' : 'read');
  const canWrite = canWriteAtLevel(eff);
  return {
    canUploadCreatorSheet: false,
    canAddSingleCreator: false,
    canApproveRejectCreators: canWrite,
    canApproveAcceptedCreators: false,
    canApproveOnBehalfOfBrand: false,
    canRemoveOnPending: false,
    canReinstateRejected: false,
    canMoveToScriptReview: false,
    canSubmitScriptOnBehalf: false,
    canReviewScript: canWrite,
    canApproveScript: canWrite, // brand is the sole script approver
    canSubmitContentOnBehalf: false,
    canReviewContent: canWrite,
    canEditLiveAnalytics: false,
    canSendBrief: false,
    canAssignProducts: false,
    canOpenConversation: true, // read can VIEW a thread; the composer is gated separately
    canManageGrants: eff === 'admin',
    canViewShareLinks: false,
    canEditCreator: false,
    canHoldCreator: false,
    canRollbackApproval: false,
    canResolvePreviousProducts: canWrite,
  };
}

/**
 * Admin campaign permissions from the effective per-campaign level. Super-admin
 * maps to admin; admin_member gets their grant/creator level. Destructive
 * role-scoped controls (e.g. roster:remove) are AND-ed with the caller's scopes
 * at the call site.
 */
export function buildAdminPermissions(
  role: 'admin' | 'admin_member' | string,
  level?: GrantLevel,
): CampaignPermissions {
  const eff: GrantLevel = level ?? (role === 'admin' ? 'admin' : 'read');
  const canWrite = canWriteAtLevel(eff);
  return {
    canUploadCreatorSheet: canWrite,
    canAddSingleCreator: canWrite,
    canApproveRejectCreators: canWrite,
    canApproveAcceptedCreators: canWrite,
    canApproveOnBehalfOfBrand: canWrite,
    canRemoveOnPending: eff === 'admin',
    canReinstateRejected: canWrite,
    canMoveToScriptReview: canWrite,
    canSubmitScriptOnBehalf: canWrite,
    canReviewScript: canWrite,
    canApproveScript: false, // admin never approves scripts — brand only
    canSubmitContentOnBehalf: canWrite,
    canReviewContent: false, // admin never approves content, brand only
    canEditLiveAnalytics: canWrite,
    canSendBrief: canWrite,
    canAssignProducts: canWrite,
    canOpenConversation: true,
    canManageGrants: eff === 'admin',
    canViewShareLinks: eff === 'admin',
    canEditCreator: canWrite,
    canHoldCreator: eff === 'admin',
    canRollbackApproval: eff === 'admin',
    canResolvePreviousProducts: canWrite,
  };
}

/** @deprecated Use buildAdminPermissions(role, level) so the grant level propagates. */
export const ADMIN_PERMISSIONS: CampaignPermissions = buildAdminPermissions('admin', 'admin');

/** @deprecated Use buildBrandPermissions(role, level) so the grant level propagates. */
export const BRAND_PERMISSIONS: CampaignPermissions = buildBrandPermissions('brand_owner', 'admin');

export interface AdminScopeGrants {
  rosterRemove: boolean;
  rosterHold: boolean;
  rosterReleaseHold: boolean;
  rosterRollbackApproval: boolean;
}

/** Narrows level-derived admin permissions by the caller's granted scopes. Hold
 *  needs both hold scopes: a hold that cannot be released must not be offered. */
export function applyAdminScopeGates(base: CampaignPermissions, grants: AdminScopeGrants): CampaignPermissions {
  return {
    ...base,
    canRemoveOnPending: base.canRemoveOnPending && grants.rosterRemove,
    canHoldCreator: base.canHoldCreator && grants.rosterHold && grants.rosterReleaseHold,
    canRollbackApproval: base.canRollbackApproval && grants.rosterRollbackApproval,
  };
}
